import React, {useEffect, useState} from "react";
import {Alert, Box, Card, CardContent, CircularProgress, Divider, Grid, Typography,} from "@mui/material";
import axios from "axios";
import {baseUrl} from "../Univ";
const VaccinationDashboard = () => {
    const [dashboard, setDashboard] = useState({
        total_vaccinations: 0,
        today_vaccinations: 0,
        pending_doses: 0,
        overdue_doses: 0,
        active_batches: 0,
        low_stock_batches: 0,
        expired_batches: 0,
        male_vaccinations: 0,
        female_vaccinations: 0,
        other_vaccinations: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const headers = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    /* ============================================================
       LOAD DASHBOARD FROM EXISTING APIs
       ============================================================ */
    useEffect(() => {
        loadDashboard();
    }, []);
    const loadDashboard = async () => {
        setLoading(true);
        setError("");
        try {
            const [administrationResponse, trackingResponse, batchResponse] = await Promise.all([axios.get(`${baseUrl}vaccination/administrations/`, {
                headers: headers(),
            }), axios.get(`${baseUrl}vaccination/tracking/`, {
                headers: headers(),
            }), axios.get(`${baseUrl}vaccination/batches/`, {
                headers: headers(),
            }),]);
            const administrations = Array.isArray(administrationResponse.data) ? administrationResponse.data : administrationResponse.data?.results || [];
            const tracking = Array.isArray(trackingResponse.data) ? trackingResponse.data : trackingResponse.data?.results || [];
            const batches = Array.isArray(batchResponse.data) ? batchResponse.data : batchResponse.data?.results || [];
            /* ====================================================
               TODAY
               ==================================================== */
            const today = new Date();
            const todayString = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
            const todayVaccinations = administrations.filter((item) => item.administered_date === todayString).length;
            /* ====================================================
               TRACKING
               ==================================================== */
            const pendingDoses = tracking.filter((item) => {
                const status = String(item.status || "").toUpperCase();
                return ["UPCOMING", "PENDING", "SCHEDULED",].includes(status);
            }).length;
            const overdueDoses = tracking.filter((item) => {
                const status = String(item.status || "").toUpperCase();
                return status === "OVERDUE";
            }).length;
            /* ====================================================
               BATCHES
               ==================================================== */
            const activeBatches = batches.filter((item) => {
                const status = String(item.status || "").toUpperCase();
                return ["ACTIVE", "AVAILABLE", "IN_STOCK",].includes(status);
            }).length;
            const lowStockBatches = batches.filter((item) => {
                const quantity = Number(item.current_stock ?? item.available_quantity ?? item.quantity ?? item.stock ?? 0);
                const minimum = Number(item.minimum_stock ?? item.reorder_level ?? item.min_stock ?? 0);
                return minimum > 0 && quantity <= minimum;
            }).length;
            const expiredBatches = batches.filter((item) => {
                const expiry = item.expiry_date || item.expiry || null;
                if (!expiry) {
                    return false;
                }
                return new Date(expiry) < today;
            }).length;
            /* ====================================================
               GENDER
               ==================================================== */
            let male = 0;
            let female = 0;
            let other = 0;
            administrations.forEach((item) => {
                const patient = typeof item.patient === "object" ? item.patient : null;
                const gender = String(patient?.gender || item.gender || "").toUpperCase();
                if (gender === "M" || gender === "MALE") {
                    male++;
                } else if (gender === "F" || gender === "FEMALE") {
                    female++;
                } else {
                    other++;
                }
            });
            /* ====================================================
               SET DASHBOARD
               ==================================================== */
            setDashboard({
                total_vaccinations: administrations.length,
                today_vaccinations: todayVaccinations,
                pending_doses: pendingDoses,
                overdue_doses: overdueDoses,
                active_batches: activeBatches,
                low_stock_batches: lowStockBatches,
                expired_batches: expiredBatches,
                male_vaccinations: male,
                female_vaccinations: female,
                other_vaccinations: other,
            });
        } catch (err) {
            console.error("Vaccination dashboard error:", err);
            setError(err.response?.data?.detail || "Unable to load vaccination dashboard.");
        } finally {
            setLoading(false);
        }
    };
    /* ============================================================
       STAT CARD
       ============================================================ */
    const StatCard = ({
                          title, value, subtitle, icon, accent,
                      }) => (<Card
        sx={{
            height: "100%",
            borderRadius: "14px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 3px 14px rgba(15, 23, 42, 0.06)",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.2s ease",
            "&:hover": {
                transform: "translateY(-2px)", boxShadow: "0 7px 22px rgba(15, 23, 42, 0.10)",
            },
            "&::before": {
                content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", background: accent,
            },
        }}
    >
        <CardContent
            sx={{
                p: 2.5, "&:last-child": {
                    pb: 2.5,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                    }}
                >
                    {title}
                </Typography>

                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `${accent}18`,
                        color: accent,
                        fontSize: "17px",
                    }}
                >
                    <i className={icon}></i>
                </Box>
            </Box>

            <Typography
                sx={{
                    mt: 2, fontSize: "30px", lineHeight: 1, fontWeight: 700, color: "#1e293b",
                }}
            >
                {value ?? 0}
            </Typography>

            <Typography
                sx={{
                    mt: 1, fontSize: "12px", color: "#94a3b8",
                }}
            >
                {subtitle}
            </Typography>
        </CardContent>
    </Card>);
    /* ============================================================
       SUMMARY ITEM
       ============================================================ */
    const SummaryItem = ({
                             title, value, icon,
                         }) => (<Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 2,
            borderRadius: "10px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
        }}
    >
        <Box
            sx={{
                width: 38,
                height: 38,
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                color: "#475569",
            }}
        >
            <i className={icon}></i>
        </Box>

        <Box>
            <Typography
                sx={{
                    fontSize: "12px", color: "#64748b",
                }}
            >
                {title}
            </Typography>

            <Typography
                sx={{
                    fontSize: "20px", fontWeight: 700, color: "#1e293b",
                }}
            >
                {value ?? 0}
            </Typography>
        </Box>
    </Box>);
    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (<Box
            sx={{
                minHeight: "55vh", display: "flex", justifyContent: "center", alignItems: "center",
            }}
        >
            <Box sx={{textAlign: "center"}}>
                <CircularProgress size={38}/>

                <Typography
                    sx={{
                        mt: 2, fontSize: "13px", color: "#64748b",
                    }}
                >
                    Loading vaccination dashboard...
                </Typography>
            </Box>
        </Box>);
    }
    /* ============================================================
       PAGE
       ============================================================ */
    return (<Box
        sx={{
            width: "100%", px: {
                xs: 1, sm: 2, md: 2.5,
            }, py: 2,
        }}
    >
        {/* ====================================================
               HEADER
               ==================================================== */}

        <Box
            sx={{
                display: "flex", alignItems: {
                    xs: "flex-start", sm: "center",
                }, justifyContent: "space-between", flexDirection: {
                    xs: "column", sm: "row",
                }, gap: 1.5, mb: 3,
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: {
                            xs: "21px", md: "24px",
                        }, fontWeight: 700, color: "#1e293b",
                    }}
                >
                    Vaccination Dashboard
                </Typography>

                <Typography
                    sx={{
                        mt: 0.5, fontSize: "13px", color: "#64748b",
                    }}
                >
                    Vaccination administration, tracking
                    and inventory overview
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                }}
            >
                <Box
                    sx={{
                        width: 8, height: 8, borderRadius: "50%", background: "#16a34a",
                    }}
                />

                <Typography
                    sx={{
                        fontSize: "12px", fontWeight: 600, color: "#15803d",
                    }}
                >
                    Vaccination Module Active
                </Typography>
            </Box>
        </Box>

        {/* ====================================================
               ERROR
               ==================================================== */}

        {error && (<Alert
            severity="error"
            sx={{
                mb: 3, borderRadius: "10px",
            }}
        >
            {error}
        </Alert>)}

        {/* ====================================================
               VACCINATION STATISTICS
               ==================================================== */}

        <Grid
            container
            spacing={2}
        >
            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Total Vaccinations"
                    value={dashboard.total_vaccinations}
                    subtitle="All administrations"
                    icon="fa-solid fa-syringe"
                    accent="#16a34a"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Today's Vaccinations"
                    value={dashboard.today_vaccinations}
                    subtitle="Administered today"
                    icon="fa-solid fa-calendar-day"
                    accent="#2563eb"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Pending Doses"
                    value={dashboard.pending_doses}
                    subtitle="Pending vaccination"
                    icon="fa-solid fa-hourglass-half"
                    accent="#f59e0b"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <StatCard
                    title="Overdue Doses"
                    value={dashboard.overdue_doses}
                    subtitle="Require attention"
                    icon="fa-solid fa-triangle-exclamation"
                    accent="#dc2626"
                />
            </Grid>
        </Grid>

        {/* ====================================================
               INVENTORY
               ==================================================== */}

        <Typography
            sx={{
                mt: 3, mb: 1.5, fontSize: "15px", fontWeight: 700, color: "#334155",
            }}
        >
            Vaccine Inventory
        </Typography>

        <Grid
            container
            spacing={2}
        >
            <Grid item xs={12} sm={6} md={4}>
                <StatCard
                    title="Active Batches"
                    value={dashboard.active_batches}
                    subtitle="Currently available"
                    icon="fa-solid fa-boxes-stacked"
                    accent="#16a34a"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <StatCard
                    title="Low Stock Batches"
                    value={dashboard.low_stock_batches}
                    subtitle="Below stock threshold"
                    icon="fa-solid fa-arrow-trend-down"
                    accent="#f59e0b"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
                <StatCard
                    title="Expired Batches"
                    value={dashboard.expired_batches}
                    subtitle="Remove from circulation"
                    icon="fa-solid fa-calendar-xmark"
                    accent="#dc2626"
                />
            </Grid>
        </Grid>

        {/* ====================================================
               DEMOGRAPHIC SUMMARY
               ==================================================== */}

        <Card
            sx={{
                mt: 3,
                borderRadius: "14px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 3px 14px rgba(15, 23, 42, 0.06)",
            }}
        >
            <CardContent
                sx={{
                    p: 2.5, "&:last-child": {
                        pb: 2.5,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "16px", fontWeight: 700, color: "#1e293b",
                            }}
                        >
                            Vaccination Summary
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.4, fontSize: "12px", color: "#94a3b8",
                            }}
                        >
                            Vaccinations by gender
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f0fdf4",
                            color: "#16a34a",
                        }}
                    >
                        <i className="fa-solid fa-users"></i>
                    </Box>
                </Box>

                <Divider sx={{mb: 2}}/>

                <Grid
                    container
                    spacing={2}
                >
                    <Grid item xs={12} md={4}>
                        <SummaryItem
                            title="Male Vaccinations"
                            value={dashboard.male_vaccinations}
                            icon="fa-solid fa-mars"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <SummaryItem
                            title="Female Vaccinations"
                            value={dashboard.female_vaccinations}
                            icon="fa-solid fa-venus"
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <SummaryItem
                            title="Other Vaccinations"
                            value={dashboard.other_vaccinations}
                            icon="fa-solid fa-user"
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>

        {/* ====================================================
               FOOTER
               ==================================================== */}

        <Box
            sx={{
                mt: 2, px: 0.5, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
            }}
        >
            <Typography
                sx={{
                    fontSize: "11px", color: "#94a3b8",
                }}
            >
                DrGHiS Vaccination Management
            </Typography>

            <Typography
                sx={{
                    fontSize: "11px", color: "#94a3b8",
                }}
            >
                Administration • Tracking • Inventory
            </Typography>
        </Box>
    </Box>);
};
export default VaccinationDashboard;